import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Filler,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import classes from "./charts.module.css";
import { useSelector } from "react-redux";
import { useMerchantInvoiceDashboardOffline } from "../../../../offline";
import {
  ALL_MONTHS,
  formatCompactNumber,
  getAvailableYears,
  getDaysInMonth,
  getMonthLabel,
  MONTH_LONG_NAMES,
  MONTH_SHORT_NAMES,
  toDateValue,
  toSafeNumber,
} from "./chartPeriodUtils";

ChartJS.register(
  Filler,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const buildAreaGradient = (context) => {
  const { chart } = context;
  const { ctx, chartArea } = chart;

  if (!chartArea) {
    return "rgba(21, 101, 216, 0.16)";
  }

  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, "rgba(21, 101, 216, 0.32)");
  gradient.addColorStop(1, "rgba(21, 101, 216, 0.02)");
  return gradient;
};

const InvoiceChart = () => {
  const Data = useSelector((state) => state.loginData.value);
  const auth_token = Data?.auth_token;
  const user_token = Data?.user?.token || Data?.user_token;
  const org_id = Data?.org_id || Data?.user?.org_id || Data?.organization_id;
  const { data: dashboardSummary } = useMerchantInvoiceDashboardOffline(
    auth_token,
    user_token,
    org_id
  );

  const summary = dashboardSummary?.invoice_summary || {};

  const dailyEntries = useMemo(
    () =>
      (summary?.daily || [])
        .map((item) => {
          const date = toDateValue(item?.day);
          return date
            ? {
                date,
                count: toSafeNumber(item?.count),
              }
            : null;
        })
        .filter(Boolean),
    [summary]
  );

  const monthlyEntries = useMemo(
    () =>
      (summary?.monthly || [])
        .map((item) => {
          const date = toDateValue(item?.month);
          return date
            ? {
                date,
                count: toSafeNumber(item?.count),
              }
            : null;
        })
        .filter(Boolean),
    [summary]
  );

  const availableYears = useMemo(() => {
    const yearlyDates = (summary?.yearly || []).map(
      (item) => `${String(item?.year || "").slice(0, 4)}-01-01`
    );
    const derivedYears = getAvailableYears([
      ...dailyEntries.map((item) => item.date),
      ...monthlyEntries.map((item) => item.date),
      ...yearlyDates,
    ]);

    return derivedYears.length ? derivedYears : [new Date().getFullYear()];
  }, [dailyEntries, monthlyEntries, summary]);

  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(ALL_MONTHS);

  useEffect(() => {
    const fallbackYear = String(availableYears[0] || "");
    if (!selectedYear || !availableYears.includes(Number(selectedYear))) {
      setSelectedYear(fallbackYear);
      setSelectedMonth(ALL_MONTHS);
    }
  }, [availableYears, selectedYear]);

  const resolvedYear = Number(selectedYear || availableYears[0] || new Date().getFullYear());
  const resolvedMonth = selectedMonth === ALL_MONTHS ? ALL_MONTHS : Number(selectedMonth);

  const activeSeries = useMemo(() => {
    if (!Number.isFinite(resolvedYear)) {
      return [];
    }

    if (resolvedMonth !== ALL_MONTHS) {
      const daysInMonth = getDaysInMonth(resolvedYear, resolvedMonth);
      const monthMap = new Map(
        dailyEntries
          .filter(
            (entry) =>
              entry.date.getFullYear() === resolvedYear &&
              entry.date.getMonth() === resolvedMonth
          )
          .map((entry) => [entry.date.getDate(), toSafeNumber(entry.count)])
      );

      return Array.from({ length: daysInMonth }, (_, index) => {
        const dayNumber = index + 1;
        return {
          label: `${dayNumber}`,
          value: monthMap.get(dayNumber) || 0,
        };
      });
    }

    const aggregatedFromDaily = new Map();
    dailyEntries
      .filter((entry) => entry.date.getFullYear() === resolvedYear)
      .forEach((entry) => {
        const monthIndex = entry.date.getMonth();
        aggregatedFromDaily.set(
          monthIndex,
          (aggregatedFromDaily.get(monthIndex) || 0) + toSafeNumber(entry.count)
        );
      });

    const fallbackMonthlyMap = new Map(
      monthlyEntries
        .filter((entry) => entry.date.getFullYear() === resolvedYear)
        .map((entry) => [entry.date.getMonth(), toSafeNumber(entry.count)])
    );

    return MONTH_SHORT_NAMES.map((label, monthIndex) => ({
      label,
      value:
        aggregatedFromDaily.size > 0
          ? aggregatedFromDaily.get(monthIndex) || 0
          : fallbackMonthlyMap.get(monthIndex) || 0,
    }));
  }, [dailyEntries, monthlyEntries, resolvedMonth, resolvedYear]);

  const chartHeading = resolvedMonth === ALL_MONTHS
    ? `Monthly volume for ${resolvedYear}`
    : `Daily volume for ${getMonthLabel(resolvedMonth)} ${resolvedYear}`;

  const totalInvoices = activeSeries.reduce(
    (sum, item) => sum + toSafeNumber(item?.value),
    0
  );

  const peakPoint = activeSeries.reduce(
    (best, item) =>
      toSafeNumber(item?.value) > toSafeNumber(best?.value) ? item : best,
    activeSeries[0] || { label: "-", value: 0 }
  );

  const averageValue = activeSeries.length
    ? Math.round(totalInvoices / activeSeries.length)
    : 0;

  const chartData = {
    labels: activeSeries.map((item) => item.label),
    datasets: [
      {
        label: "Invoices",
        data: activeSeries.map((item) => item.value),
        borderColor: "#1565d8",
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBorderWidth: 2,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#1565d8",
        tension: 0.36,
        fill: true,
        backgroundColor: buildAreaGradient,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(9, 20, 42, 0.94)",
        borderColor: "rgba(120, 168, 255, 0.24)",
        borderWidth: 1,
        padding: 14,
        displayColors: false,
        titleColor: "#dbeafe",
        bodyColor: "#ffffff",
        callbacks: {
          label: (context) =>
            `Invoices: ${formatCompactNumber(context.parsed?.y || context.raw || 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#47607f",
          font: {
            family: "gilroyMedium",
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawBorder: false,
        },
        ticks: {
          color: "#47607f",
          precision: 0,
          font: {
            family: "gilroyMedium",
            size: 11,
          },
          callback: (value) => formatCompactNumber(value),
        },
      },
    },
  };

  return (
    <section className={classes.invoiceChartWrapper}>
      <div className={classes.chartHeader}>
        <div>
          <h2 className={classes.chartTitle}>Invoice Trends</h2>
          <p className={classes.chartSubtitle}>{chartHeading}</p>
        </div>

        <div className={classes.chartFilters}>
          <label className={classes.chartFilterField}>
            <span className={classes.chartFilterLabel}>Year</span>
            <select
              value={selectedYear}
              onChange={(event) => {
                setSelectedYear(event.target.value);
                setSelectedMonth(ALL_MONTHS);
              }}
              className={classes.chartFilterSelect}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>

          <label className={classes.chartFilterField}>
            <span className={classes.chartFilterLabel}>Month</span>
            <select
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              className={classes.chartFilterSelect}
            >
              <option value={ALL_MONTHS}>All Months</option>
              {MONTH_LONG_NAMES.map((monthName, index) => (
                <option key={monthName} value={index}>
                  {monthName}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className={classes.chartInsightStrip}>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Total Volume</span>
          <strong className={classes.chartInsightValue}>
            {formatCompactNumber(totalInvoices)}
          </strong>
        </div>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Peak Window</span>
          <strong className={classes.chartInsightValue}>
            {peakPoint?.label || "-"}: {formatCompactNumber(peakPoint?.value || 0)}
          </strong>
        </div>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Average</span>
          <strong className={classes.chartInsightValue}>
            {formatCompactNumber(averageValue)}
          </strong>
        </div>
      </div>

      <div className={classes.chartContainer}>
        {activeSeries.some((item) => item.value > 0) ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className={classes.chartEmptyState}>
            No invoice data available for the selected period.
          </div>
        )}
      </div>
    </section>
  );
};

export default InvoiceChart;
